import { storage } from '../../utils/storage.js';
import { getVisibleMessages } from './message-tree.js';

const isValidConversationId = (conversationId) => {
    return typeof conversationId === 'string' && conversationId.trim().length > 0;
};

export const getGlobalHistoryMessages = () => {
    const localHistory = storage.get('hangout:history');
    return Array.isArray(localHistory) ? localHistory : [];
};

export const getTranscriptForLayout = (hangoutState) => {
    const visible = hangoutState?.getVisibleMessages?.();
    if (Array.isArray(visible)) return visible;
    const allMessages = hangoutState?.getMessages?.();
    return Array.isArray(allMessages) ? allMessages : [];
};

export const getConversationBucketMessages = (conversationId) => {
    if (!isValidConversationId(conversationId)) return [];
    const conversationMessages = storage.getConversationMessages(conversationId);
    return Array.isArray(conversationMessages) ? conversationMessages : [];
};

export const resolvePreferredLocalMessages = (
    conversationId
) => {
    const globalHistory = getGlobalHistoryMessages();
    if (globalHistory.length > 0) {
        return { messages: globalHistory, source: 'hangout:history' };
    }

    const bucketMessages = getConversationBucketMessages(conversationId);
    if (bucketMessages.length > 0) {
        return { messages: bucketMessages, source: 'conversation_history' };
    }

    return { messages: [], source: 'none' };
};

export const hydrateGlobalHistoryFromConversation = (conversationId) => {
    const globalHistory = getGlobalHistoryMessages();
    if (globalHistory.length > 0) {
        return { messages: globalHistory, hydrated: false };
    }

    const bucketMessages = getConversationBucketMessages(conversationId);
    if (bucketMessages.length === 0) {
        return { messages: [], hydrated: false };
    }

    const hydrated = storage.replaceConversationMessagesAtomic(conversationId, bucketMessages, {
        reason: 'hydrate-from-conversation-bucket',
        enforceAllOrNothing: true
    });
    if (!hydrated) {
        console.warn('[Hangout][History] Bucket hydration aborted by atomic guard', {
            conversationId,
            bucketCount: bucketMessages.length
        });
        return { messages: globalHistory, hydrated: false };
    }
    return { messages: bucketMessages, hydrated: true };
};

export const shouldFetchConversationFromBackend = (
    conversationId
) => {
    const { messages } = resolvePreferredLocalMessages(conversationId);
    return messages.length === 0;
};

export const normalizeConversationHistory = (messages) => {
    if (!Array.isArray(messages)) return [];

    return messages
        .map((message) => {
            if (!message || typeof message !== 'object') return null;
            const normalizedRole = typeof message.role === 'string' ? message.role.trim().toLowerCase() : '';
            const normalizedContent = typeof message.content === 'string'
                ? message.content.trim()
                : String(message.content ?? '').trim();

            if (!normalizedRole || !normalizedContent) return null;
            return {
                ...message,
                role: normalizedRole,
                content: normalizedContent
            };
        })
        .filter(Boolean);
};

export const filterHistoryToActiveBranch = (messages, hangoutState) => {
    const candidates = Array.isArray(messages) ? messages : [];
    if (!hangoutState) return normalizeConversationHistory(candidates);

    const activeVersions = hangoutState.getActiveVersions?.() || {};
    const pendingBranch = hangoutState.state?.pendingBranch || null;
    const branchTipPath = hangoutState.getBranchTipPath?.() || null;
    const greetingVariation = hangoutState.getGreetingVariationMeta?.()?.variation;
    return normalizeConversationHistory(
        getVisibleMessages(candidates, activeVersions, { pendingBranch, branchTipPath, greetingVariation })
    );
};

export async function resolveConversationHistory({ hangoutState, history = null }) {
    const visibleFromState = normalizeConversationHistory(hangoutState?.getVisibleMessages?.() || []);
    if (visibleFromState.length > 0) return visibleFromState;

    const explicitHistory = normalizeConversationHistory(history);
    if (explicitHistory.length > 0) {
        const filteredExplicit = filterHistoryToActiveBranch(explicitHistory, hangoutState);
        if (filteredExplicit.length > 0) return filteredExplicit;
        return explicitHistory;
    }

    const storedMessages = normalizeConversationHistory(getGlobalHistoryMessages());
    if (storedMessages.length > 0) {
        const filteredStored = filterHistoryToActiveBranch(storedMessages, hangoutState);
        if (filteredStored.length > 0) return filteredStored;
        return storedMessages;
    }

    const visibleFallback = normalizeConversationHistory(hangoutState?.getVisibleMessages?.() || []);
    if (visibleFallback.length > 0) return visibleFallback;

    return normalizeConversationHistory(hangoutState?.getMessages?.() || []);
};
